import { HTMLAttributeAnchorTarget } from "react";

import classNames from "classnames/bind";
import SmartLink from "common/wrapper/SmartLink";

import UIIcon from "../UIIcon";
import styles from "./Button.module.scss";

export const SIZES = ["xsmall", "small", "medium", "large", "slim"] as const;
export const THEMES = [
    "primary",
    "secondary",
    "tertiary",
    "selection",
    "light",
    "textLink",
    "accent",
] as const;

const cx = classNames.bind(styles);

export interface ButtonProps {
    ariaLabel?: string;
    children?: React.ReactNode;
    className?: string;
    fullWidth?: boolean;
    href?: string;
    isDisabled?: boolean;
    isSelected?: boolean;
    onClick?: (e?: CtaClickEvent) => void;
    size?: (typeof SIZES)[number];
    theme?: (typeof THEMES)[number];
    type?: "button" | "submit" | "reset";
    rel?: string;
    target?: HTMLAttributeAnchorTarget;
    prefetch?: boolean;
    forceActive?: boolean;
    id?: string;
    loading?: boolean;
    autoWidth?: boolean;
    rightIcon?: React.ReactElement;
    title?: string;
    dataTestId?: string;
}

export type CtaClickEvent = React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>;

const Button: React.FC<ButtonProps> = ({
    ariaLabel,
    children,
    className,
    fullWidth = false,
    href,
    isDisabled = false,
    isSelected = false,
    onClick,
    size = "medium",
    theme = "primary",
    type = "button",
    prefetch = false,
    rel,
    target,
    forceActive = false,
    id,
    loading,
    autoWidth = false,
    rightIcon,
    title,
    dataTestId,
}) => {
    const attrs = {
        "aria-label": ariaLabel,
        className: cx(
            "button",
            { fullWidth, isDisabled, isSelected, forceActive, autoWidth },
            size,
            theme,
            className
        ),
        disabled: isDisabled,
        onClick,
        type,
        "data-loading": loading,
        title,
        ...(dataTestId ? { "data-testid": dataTestId } : {}),
    };

    if (!href) {
        return (
            <button id={id} {...attrs}>
                <div>{children}</div>
                {loading && <UIIcon icon="CircleAnimation" className={cx("loadingAnimation")} />}
                {rightIcon && <div className={cx("iconWrapper")}>{rightIcon}</div>}
            </button>
        );
    }

    return (
        <SmartLink href={href} prefetch={!!prefetch}>
            <a id={id} {...attrs} rel={rel} target={target}>
                {children}
            </a>
        </SmartLink>
    );
};

export default Button;
